import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FiCalendar, FiClock, FiSearch, FiTag, FiChevronLeft, FiChevronRight, FiThumbsUp } from 'react-icons/fi'
import { RootState, AppDispatch } from '@/store/store'
import {
    fetchPublishedPosts,
    fetchCategories,
    fetchTags,
    searchBlogs,
    fetchByCategory,
    fetchByTag,
    setActiveFilters,
    setCurrentPage,
    clearSearch,
    likeBlog
} from '@/store/slices/blogSlice'
import { IBlogPost } from '@/types/blog'
import { getCategoryName, formatBlogDate } from '@/utils/blogHelpers'

const BlogPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [selectedCategory, setSelectedCategory] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [isLocalSearching, setIsLocalSearching] = useState(false)

    const {
        posts,
        isLoading,
        pagination,
        categories,
        tags,
        isLoadingCategories,
        activeFilters,
        featuredPosts,
        isLoadingFeatured,
        likedPosts
    } = useSelector((state: RootState) => state.blog)

    // Fetch categories and featured posts on mount
    useEffect(() => {
        dispatch(fetchCategories())
        dispatch(fetchTags())
        dispatch(fetchPublishedPosts({ page: 1, limit: 10 }))
    }, [dispatch])

    // Handle category change
    const handleCategoryChange = (categoryId: string) => {
        setSelectedCategory(categoryId)
        setSearchQuery('')
        dispatch(clearSearch())

        if (categoryId === 'all') {
            dispatch(fetchPublishedPosts({ page: 1, limit: 10 }))
        } else {
            dispatch(fetchByCategory({ category: categoryId, filters: { page: 1, limit: 10 } }))
        }
    }

    // Handle search
    const handleSearch = (query: string) => {
        setSearchQuery(query)
        setIsLocalSearching(true)

        if (query.trim()) {
            dispatch(searchBlogs({ query, filters: { page: 1, limit: 10 } }))
        } else {
            dispatch(clearSearch())
            if (selectedCategory === 'all') {
                dispatch(fetchPublishedPosts({ page: 1, limit: 10 }))
            } else {
                dispatch(fetchByCategory({ category: selectedCategory, filters: { page: 1, limit: 10 } }))
            }
        }
    }

    // Handle pagination
    const handlePageChange = (newPage: number) => {
        dispatch(setCurrentPage(newPage))
        const filters = { ...activeFilters, page: newPage }
        dispatch(setActiveFilters(filters))

        if (searchQuery.trim()) {
            dispatch(searchBlogs({ query: searchQuery, filters }))
        } else if (selectedCategory !== 'all') {
            dispatch(fetchByCategory({ category: selectedCategory, filters }))
        } else {
            dispatch(fetchPublishedPosts(filters))
        }
    }

    // Handle like
    const handleLike = (postId: string) => {
        dispatch(likeBlog(postId))
    }

    // Navigate to post detail
    const handleReadMore = (post: IBlogPost) => {
        navigate(`/blog/${post.slug}`)
    }



    // Generate category options with counts
    const categoryOptions = [
        { id: 'all', label: 'All Posts', count: pagination?.totalCount ?? 0 }
    ]

    if (Array.isArray(categories) && categories.length > 0) {
        categories.forEach(cat => {
            const catName = getCategoryName(cat)
            const catId = typeof cat === 'object' ? cat._id : cat
            // Count posts in category (in real app, this would come from backend)
            const count = posts.filter(p => {
                const postCatName = getCategoryName(p.category)
                return postCatName === catName
            }).length
            categoryOptions.push({ id: catId, label: catName.charAt(0).toUpperCase() + catName.slice(1), count })
        })
    }

    const displayedPosts = posts || []

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="heading-hero text-gray-900 dark:text-white mb-6">SideGO Blog</h1>
                    <p className="text-body-lg text-gray-700 dark:text-gray-300 mb-8">
                        Insights, tips, and stories to help you succeed in your freelance journey.
                        Stay updated with the latest trends, platform updates, and success strategies.
                    </p>

                    {/* Search Bar */}
                    <div className="max-w-xl mx-auto">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search articles..."
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="form-input pl-12 text-lg"
                            />
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FiSearch className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Category Filter */}
            <section className="py-8 border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {isLoadingCategories ? (
                        <div className="text-center text-gray-500">Loading categories...</div>
                    ) : (
                        <div className="flex flex-wrap justify-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                            {categoryOptions.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => handleCategoryChange(category.id)}
                                    className={`px-4 py-2 rounded-full text-ui whitespace-nowrap transition-colors duration-200 ${selectedCategory === category.id
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                >
                                    {category.label} ({category.count})
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Featured Post Section */}
            {featuredPosts.length > 0 && selectedCategory === 'all' && !searchQuery && (
                <section className="py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {featuredPosts.map((featuredPost) => (
                            <div
                                key={featuredPost._id}
                                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm"
                            >
                                <div className="grid grid-cols-1 lg:grid-cols-2">
                                    {/* Image Section */}
                                    <div className="w-full h-64 lg:h-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                                        {featuredPost.heroImage ? (
                                            <img
                                                src={featuredPost.heroImage}
                                                alt={featuredPost.heroImageAlt || featuredPost.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="text-center">
                                                <div className="text-6xl font-heading font-bold mb-2">📝</div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Featured Article</div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Content Section */}
                                    <div className="p-8">
                                        <div className="flex items-center mb-4 gap-2">
                                            <span className="inline-block bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs px-3 py-1 rounded-full font-medium">
                                                ⭐ Featured
                                            </span>
                                            <span className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-3 py-1 rounded-full capitalize">
                                                {getCategoryName(featuredPost.category)}
                                            </span>
                                        </div>

                                        <h2 className="heading-section text-gray-900 dark:text-white mb-4">{featuredPost.title}</h2>
                                        <p className="text-body text-gray-700 dark:text-gray-300 mb-6">{featuredPost.excerpt}</p>

                                        <div className="flex items-center mb-6 text-ui-sm text-gray-600 dark:text-gray-400 flex-wrap gap-4">
                                            <div className="flex items-center">
                                                <FiCalendar className="w-4 h-4 mr-2" />
                                                <span>{formatBlogDate(featuredPost.publishedAt)}</span>
                                            </div>
                                            <div className="flex items-center">
                                                <FiClock className="w-4 h-4 mr-2" />
                                                <span>{featuredPost.readingTime} min read</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-6">
                                            {featuredPost.tags.slice(0, 4).map((tag) => (
                                                <span key={tag} className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded-full">
                                                    #{tag.toLowerCase().replace(/\s+/g, '')}
                                                </span>
                                            ))}
                                        </div>

                                        <button
                                            onClick={() => handleReadMore(featuredPost)}
                                            className="btn-primary"
                                        >
                                            Read Full Article
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Blog Posts Grid */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {searchQuery && (
                        <div className="mb-8">
                            <p className="text-body text-gray-700 dark:text-gray-300">
                                Found {displayedPosts.length} article{displayedPosts.length !== 1 ? 's' : ''} for "{searchQuery}"
                            </p>
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex justify-center items-center py-16">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary-600"></div>
                        </div>
                    ) : displayedPosts.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-gray-500 dark:text-gray-400">
                                <p className="text-lg mb-2">No articles found</p>
                                <p className="text-sm">Try adjusting your search or filter criteria</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {displayedPosts.map((post) => (
                                    <article
                                        key={post._id}
                                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                                    >
                                        {/* Image */}
                                        <div className="w-full h-48 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                                            {post.heroImage ? (
                                                <img
                                                    src={post.heroImage}
                                                    alt={post.heroImageAlt || post.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="text-center">
                                                    <div className="text-3xl">📄</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="p-6">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2.5 py-1 rounded-full capitalize">
                                                    {getCategoryName(post.category)}
                                                </span>
                                                {post.featured && (
                                                    <span className="text-yellow-500">⭐</span>
                                                )}
                                            </div>

                                            <h3 className="heading-subsection text-gray-900 dark:text-white mb-3 line-clamp-2 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer">
                                                {post.title}
                                            </h3>

                                            <p className="text-body-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                                                {post.excerpt}
                                            </p>

                                            {/* Meta Info */}
                                            <div className="flex items-center justify-between text-ui-sm text-gray-500 dark:text-gray-400 mb-4 flex-wrap gap-2">
                                                <div className="flex items-center">
                                                    <FiClock className="w-3 h-3 mr-1" />
                                                    <span>{post.readingTime} min</span>
                                                </div>
                                            </div>

                                            {/* Tags */}
                                            <div className="flex flex-wrap gap-1 mb-4">
                                                {post.tags.slice(0, 2).map((tag) => (
                                                    <span key={tag} className="inline-block bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-1 rounded">
                                                        #{tag}
                                                    </span>
                                                ))}
                                                {post.tags.length > 2 && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">+{post.tags.length - 2} more</span>
                                                )}
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                                                <button
                                                    onClick={() => handleLike(post._id)}
                                                    className={`flex items-center gap-1 px-3 py-1 rounded-lg transition-colors ${likedPosts.includes(post._id)
                                                        ? 'text-red-600 bg-red-50 dark:bg-red-900 dark:text-red-300'
                                                        : 'text-gray-600 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:text-red-300'
                                                        }`}
                                                >
                                                    <FiThumbsUp className="w-4 h-4" />
                                                    <span className="text-xs">{post.likes}</span>
                                                </button>

                                                <button
                                                    onClick={() => handleReadMore(post)}
                                                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium text-ui transition-colors"
                                                >
                                                    Read More →
                                                </button>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>

                            {/* Pagination */}
                            {pagination && pagination.totalPages > 1 && (
                                <div className="mt-12 flex justify-center items-center gap-2">
                                    <button
                                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                                        disabled={pagination.currentPage === 1}
                                        className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <FiChevronLeft className="w-5 h-5" />
                                    </button>

                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            className={`px-4 py-2 rounded-lg transition-colors ${pagination.currentPage === page
                                                ? 'bg-primary-600 text-white'
                                                : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                                        disabled={pagination.currentPage === pagination.totalPages}
                                        className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <FiChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    )
}

export default BlogPage
